import React, { useState, useEffect, useMemo } from "react";
import {
  Search,
  Plus,
  Wrench,
  Box,
  CheckCircle2,
  LayoutGrid,
  Upload,
  AlertTriangle,
  X,
  Pencil,
  Check,
} from "lucide-react";

import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import AssignEquipment from "../components/equipment/AssignEquipment";
import CheckInEquipment from "../components/equipment/CheckInEquipment";
import AddEquipment from "../components/equipment/AddEquipment";
import PageTitle from "components/generic/PageTitle";
import PageHeaderBreadcrumbs from "components/generic/Breadcrumbs";
import EquipmentDetailsModal from "components/equipment/EquipmentDetailsModal";
import BulkUploadEquipment from "./BulkUploadEquipment";
import Tabs from "components/generic/FilterTabs";

import {
  getAllEquipment,
  publishEquipment,
  updateEquipmentQuantity,
  getEquipmentById,
  updateEquipment
} from "services/equipmentService";
import { toast } from "react-hot-toast";

const normalizeStatus = (status) => {
  const statusMap = {
    1: "Available",
    2: "Maintenance",
    3: "Retired",
    4: "In Use",
  };
  return statusMap[status] || "Available";
};

const StatusBadge = ({ status }) => {
  const normalizedStatus = normalizeStatus(status);

  const styles = {
    Available:
      "text-muted-foreground bg-green-50 border-green-100 dark:bg-green-900/20 dark:border-green-900/30 dark:text-green-400",
    "In Use":
      "text-orange-600 bg-orange-50 border-orange-100 dark:bg-orange-900/20 dark:border-orange-900/30 dark:text-orange-400",
    Maintenance:
      "text-red-600 bg-red-50 border-red-100 dark:bg-red-900/20 dark:border-red-900/30 dark:text-red-400",
  };

  return (
    <span
      className={`px-2 py-1 rounded-full text-xs font-medium border ${styles[normalizedStatus] || styles.Available}`}
    >
      {normalizedStatus === "Available" && "○ Available"}
      {normalizedStatus === "In Use" && "○ In Use"}
      {normalizedStatus === "Maintenance" && "⚡ Maintenance"}
    </span>
  );
};

const EquipmentPage = () => {
  // --- States ---
  const [equipment, setEquipment] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [page, setPage] = useState(1);
  const [limit] = useState(50);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [editingEquipmentId, setEditingEquipmentId] = useState(null);

  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [isUpdating, setIsUpdating] = useState(false);

  const [isBulkUploading, setIsBulkUploading] = useState(false);
  const [selectedEquipment, setSelectedEquipment] = useState(null);
  const [checkInEquipment, setCheckInEquipment] = useState(null);
  const [isAddingEquipment, setIsAddingEquipment] = useState(false);

  const [viewEquipment, setViewEquipment] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedTab, setSelectedTab] = useState("All Equipments");

  const [statistics, setStatistics] = useState({
    total: 0,
    available: 0,
    inUse: 0,
    maintenance: 0,
  });

  // --- Configuration ---
  const availableTabs = ["All Equipments", "Draft"];

  const handleSaveQuantity = async (equipmentId) => {
    try {
      setIsUpdating(true);
      const payload = {
        equipment_id: equipmentId,
        quantity: parseInt(editValue, 10),
      };

      const result = await updateEquipmentQuantity(payload);

      if (result) {
        toast.success("Quantity updated successfully");
        setEditingId(null);
        // Refresh the list to show updated totals/stats
        fetchEquipment(1, false);
      }
    } catch (err) {
      toast.error(err.message || "Failed to update quantity");
    } finally {
      setIsUpdating(false);
    }
  };

  const handlePublishEquipment = async (item) => {
    try {
      const result = await publishEquipment(item.equipment_id);

      if (result) {
        // SUCCESS CUSTOM TOAST
        toast.custom(
          (t) => (
            <div
              className={`${t.visible ? "animate-enter" : "animate-leave"} max-w-md w-full bg-[#F0FDF4] border border-[#22C55E] shadow-lg rounded-xl pointer-events-auto flex relative overflow-hidden`}
            >
              <div className="flex-1 p-4">
                <div className="flex items-start">
                  <div className="flex-shrink-0 pt-0.5">
                    <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                      <CheckCircle2 className="h-6 w-6 text-[#22C55E]" />
                    </div>
                  </div>
                  <div className="ml-3 flex-1 pr-6">
                    <p className="text-base font-bold text-gray-900">
                      Publish Successful
                    </p>
                    <p className="mt-1 text-sm text-gray-500">
                      {item.equipment_name} has been published successfully!
                    </p>
                  </div>
                </div>
              </div>
              <button
                onClick={() => toast.dismiss(t.id)}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          ),
          { position: "bottom-right" },
        );

        fetchEquipment(1, false);
        setShowModal(false);
      }
    } catch (err) {
      // ERROR CUSTOM TOAST
      toast.custom(
        (t) => (
          <div
            className={`${t.visible ? "animate-enter" : "animate-leave"} max-w-md w-full bg-[#FEF2F2] border border-[#EF4444] shadow-lg rounded-xl pointer-events-auto flex relative overflow-hidden`}
          >
            <div className="flex-1 p-4">
              <div className="flex items-start">
                <div className="flex-shrink-0 pt-0.5">
                  <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                    <AlertTriangle className="h-6 w-6 text-[#EF4444]" />
                  </div>
                </div>
                <div className="ml-3 flex-1 pr-6">
                  <p className="text-base font-bold text-gray-900">
                    Publishing Failed
                  </p>
                  <p className="mt-1 text-sm text-gray-500">
                    {err.message || "Failed to publish equipment"}
                  </p>
                </div>
              </div>
            </div>
            <button
              onClick={() => toast.dismiss(t.id)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        ),
        { position: "bottom-right" },
      );
    }
  };

  // --- Fetching Data ---
  const fetchEquipment = async (pageToFetch = 1, append = false) => {
    try {
      if (append) setIsLoadingMore(true);
      else setLoading(true);

      setError(null);
      const res = await getAllEquipment(limit, pageToFetch);

      const success = res?.data?.success ?? res?.success ?? true;
      const items = res?.data?.data ?? res?.data ?? res?.data?.items ?? [];
      const summary = res?.summary ?? {};

      if (success) {
        if (append) {
          setEquipment((prev) => [...prev, ...items]);
        } else {
          setEquipment(items);
        }

        if (summary) {
          setStatistics({
            total: summary.total_equipment || 0,
            available: summary.available_equipment || 0,
            inUse: summary.in_use_equipment || 0,
            maintenance: summary.maintenance_equipment || 0,
          });
        }

        setHasMore(Array.isArray(items) ? items.length === limit : false);
      } else {
        const errMsg =
          res?.data?.error || res?.error || "Failed to fetch equipment";
        setError(errMsg);
      }
    } catch (err) {
      console.error("Fetch equipment error", err);
      setError("Failed to fetch equipment");
    } finally {
      setLoading(false);
      setIsLoadingMore(false);
    }
  };

  useEffect(() => {
    fetchEquipment(1, false);
  }, []);

  // --- Filtering Logic (Strict Separation) ---
  const filteredEquipment = useMemo(() => {
    return equipment.filter((item) => {
      const isDraftItem = item.is_draft === 1;

      // 1. Tab Filtering logic
      if (selectedTab === "All Equipments") {
        if (isDraftItem) return false;
      } else if (selectedTab === "Draft") {
        if (!isDraftItem) return false;
      }

      // 2. Search Filtering logic
      if (searchTerm.trim() === "") return true;

      const query = searchTerm.toLowerCase();
      const name = (item.equipment_name || "").toLowerCase();
      const model = (item.model_number || "").toLowerCase();
      const manufacturer = (item.manufacturer || "").toLowerCase();

      return (
        name.includes(query) ||
        model.includes(query) ||
        manufacturer.includes(query)
      );
    });
  }, [equipment, selectedTab, searchTerm]);

  // --- Event Handlers ---
  const loadMore = async () => {
    if (!hasMore) return;
    const nextPage = page + 1;
    setPage(nextPage);
    await fetchEquipment(nextPage, true);
  };

  const openEquipmentModal = (item) => {
    setViewEquipment(item);
    setShowModal(true);
  };

  const equipmentStats = [
    {
      label: "Total Equipment",
      value: statistics.total.toString(),
      icon: Box,
      color: "text-blue-600 dark:text-blue-400",
      bg: "bg-blue-50 dark:bg-blue-900/20",
    },
    {
      label: "Available",
      value: statistics.available.toString(),
      icon: CheckCircle2,
      color: "text-green-600 dark:text-green-400",
      bg: "bg-green-50 dark:bg-green-900/20",
    },
    {
      label: "In Use",
      value: statistics.inUse.toString(),
      icon: LayoutGrid,
      color: "text-orange-600 dark:text-orange-400",
      bg: "bg-orange-50 dark:bg-orange-900/20",
    },
    {
      label: "Maintenance",
      value: statistics.maintenance.toString(),
      icon: Wrench,
      color: "text-red-600 dark:text-red-400",
      bg: "bg-red-50 dark:bg-red-900/20",
    },
  ];

  // --- View Switching ---
  if (isAddingEquipment || editingEquipmentId) {
    return (
      <AddEquipment
        equipmentId={editingEquipmentId} // Pass the ID if editing
        onBack={() => {
          setIsAddingEquipment(false);
          setEditingEquipmentId(null);
          fetchEquipment(1, false); // Refresh list when coming back
        }}
      />
    );
  }

  if (isBulkUploading)
    return (
      <BulkUploadEquipment
        onBack={() => setIsBulkUploading(false)}
        onComplete={() => {
          setIsBulkUploading(false);
          fetchEquipment(1, false);
        }}
      />
    );

  if (selectedEquipment)
    return (
      <AssignEquipment
        onBack={() => setSelectedEquipment(null)}
        equipmentId={selectedEquipment.equipment_id}
        equipmentName={selectedEquipment.equipment_name}
      />
    );

  if (checkInEquipment)
    return (
      <CheckInEquipment
        onBack={() => setCheckInEquipment(null)}
        equipmentName={checkInEquipment.equipment_name || checkInEquipment.name}
      />
    );

  return (
    <div className="p-6 max-w-[1600px] mx-auto">
      <PageHeaderBreadcrumbs />

      <div className="flex justify-between items-start mb-8">
        <PageTitle
          title={"Equipment Management"}
          description={"Track and manage equipment across all locations."}
        />
        <div className="flex gap-2">
          <Button
            variant="outline"
            className="gap-2"
            onClick={() => setIsBulkUploading(true)}
          >
            <Upload size={20} /> Bulk Upload
          </Button>
          <Button
            className="bg-button-coloured gap-2"
            onClick={() => setIsAddingEquipment(true)}
          >
            <Plus size={20} /> Add Equipment
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {equipmentStats.map((stat, index) => (
          <div
            key={index}
            className="bg-card p-4 rounded-xl border border-border shadow-sm flex items-center justify-between"
          >
            <div>
              <p className="text-muted-foreground text-sm font-medium mb-1">
                {stat.label}
              </p>
              <h3 className="text-2xl font-bold text-foreground">
                {stat.value}
              </h3>
            </div>
            <div className={`p-3 rounded-lg ${stat.bg}`}>
              <stat.icon className={`w-6 h-6 ${stat.color}`} />
            </div>
          </div>
        ))}
      </div>

      <div className="bg-card p-4 rounded-xl border border-border shadow-sm mb-6">
        <div className="flex flex-col md:flex-row justify-between items-center gap-4">
          <Tabs
            tabs={availableTabs}
            value={selectedTab}
            onChange={setSelectedTab}
          />
          <div className="relative w-full md:w-80">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground w-4 h-4" />
            <Input
              placeholder="Search equipment..."
              className="pl-9 bg-background border-input focus:bg-background"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
      </div>

      {loading && page === 1 && (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-background"></div>
          <p className="text-muted-foreground mt-4">Loading equipment...</p>
        </div>
      )}

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-900/30 rounded-lg p-4 mb-6">
          <p className="text-red-600 dark:text-red-400 text-sm">{error}</p>
        </div>
      )}

      {!loading && !error && (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {filteredEquipment.length === 0 ? (
              <div className="col-span-full text-center py-12">
                <Box className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground mb-1">
                  No {selectedTab.toLowerCase()} found.
                </p>
                {/* ADDED: Logic to suggest loading more if backend has more data */}
                {hasMore && (
                  <p className="text-xs text-muted-foreground">
                    Try clicking "View More" below to load more data and check
                    for more {selectedTab.toLowerCase()}.
                  </p>
                )}
              </div>
            ) : (
              filteredEquipment.map((item) => {
                const normalizedStatus = normalizeStatus(
                  item.initial_status_id,
                );
                const isEditing = editingId === item.equipment_id;
                const itemName =
                  item.equipment_name || item.name || "Unknown Equipment";
                const itemModel =
                  item.model_number || item.equipment_type || "N/A";
                const itemPrice = item.daily_rental_rate
                  ? `$${item.daily_rental_rate}/day`
                  : "N/A";
                const lastMaintenance = item.last_maintenance_date || "N/A";

                return (
                  <div
                    key={item.equipment_id}
                    className="bg-card rounded-xl border border-border shadow-sm p-6"
                  >
                    <div className="flex justify-between items-start mb-4">
                      <div className="flex gap-4">
                        <div className="w-12 h-12 bg-muted rounded-lg flex items-center justify-center text-muted-foreground font-semibold">
                          {itemName.substring(0, 2).toUpperCase()}
                        </div>
                        <div>
                          <div className="flex items-center gap-3 mb-1">
                            <h3 className="font-bold text-foreground">
                              {itemName}
                            </h3>
                            <StatusBadge status={item.initial_status_id} />
                          </div>
                          <p className="text-sm text-muted-foreground">
                            {itemModel}
                          </p>
                        </div>
                      </div>
                      <div className="text-sm text-muted-foreground">
                        <div className="flex items-center gap-2">
                          <strong>Total Units:</strong>
                          {isEditing ? (
                            <div className="flex items-center gap-1">
                              <Input
                                type="number"
                                className="w-16 h-8 p-1 text-xs"
                                value={editValue}
                                onChange={(e) => setEditValue(e.target.value)}
                                autoFocus
                              />
                              <button
                                onClick={() =>
                                  handleSaveQuantity(item.equipment_id)
                                }
                                disabled={isUpdating}
                                className="text-green-600 hover:bg-green-50 p-1 rounded"
                              >
                                <Check size={16} />
                              </button>
                              <button
                                onClick={() => setEditingId(null)}
                                className="text-red-600 hover:bg-red-50 p-1 rounded"
                              >
                                <X size={16} />
                              </button>
                            </div>
                          ) : (
                            <div className="flex items-center gap-2 group">
                              <span>{item.quantity || 0}</span>
                              <button
                                onClick={() => {
                                  setEditingId(item.equipment_id);
                                  setEditValue(item.quantity || 0);
                                }}
                                className="opacity-0 group-hover:opacity-100 transition-opacity text-blue-600"
                              >
                                <Pencil size={14} />
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {item.assigned_to && (
                      <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-100 dark:border-yellow-900/30 rounded-lg p-3 mb-4">
                        <p className="text-sm text-foreground font-medium mb-1">
                          Assigned to: {item.assigned_to}
                        </p>
                        {item.project_name && (
                          <p className="text-xs text-muted-foreground">
                            Project: {item.project_name}
                          </p>
                        )}
                      </div>
                    )}

                    {item.accessories &&
                      Array.isArray(item.accessories) &&
                      item.accessories.length > 0 && (
                        <div className="flex flex-wrap gap-2 mb-6">
                          {item.accessories.map((acc, i) => (
                            <span
                              key={i}
                              className="px-2 py-1 bg-muted text-muted-foreground text-xs rounded-md border border-border flex items-center gap-1"
                            >
                              {i === 0 && <LayoutGrid className="w-3 h-3" />}
                              {acc}
                            </span>
                          ))}
                        </div>
                      )}

                    <div className="flex items-center justify-between mb-4 py-3 border-t border-b border-border">
                      <div className="flex items-center w-full">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-foreground">
                            {itemPrice}
                          </span>
                        </div>
                        <div className="ml-auto flex items-center gap-4 text-sm text-muted-foreground">
                          <div className="text-green-600">
                            Available: {item.units_available}
                          </div>
                          <div className="text-orange-600">
                            In Use: {item.units_in_use}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center justify-between mb-6">
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <span>Last maintenance: {lastMaintenance}</span>
                      </div>
                    </div>

                    <div className="flex gap-3">
                      {item.is_draft === 1 ? (
                        <Button
                          className="flex-1 bg-button-coloured rounded-lg text-button-coloured-foreground"
                          onClick={() =>
                            setEditingEquipmentId(item.equipment_id)
                          }
                        >
                          Edit Draft
                        </Button>
                      ) : (
                        <>
                          <button
                            className="flex-1 bg-button-generic py-2 px-4 rounded-lg text-sm font-medium hover:bg-opacity-80 transition-colors"
                            onClick={() =>
                              setEditingEquipmentId(item.equipment_id)
                            }
                          >
                            Edit Details
                          </button>
                          <button
                            className="flex-1 bg-button-generic py-2 px-4 rounded-lg text-sm font-medium hover:bg-opacity-80 transition-colors"
                            onClick={() => openEquipmentModal(item)}
                          >
                            View Details
                          </button>
                          <Button
                            className="bg-button-coloured rounded-lg text-button-coloured-foreground flex-1"
                            onClick={() => {
                              if (normalizedStatus === "Available")
                                setSelectedEquipment(item);
                              else if (normalizedStatus === "In Use")
                                setCheckInEquipment(item);
                            }}
                            disabled={normalizedStatus === "Maintenance"}
                          >
                            {normalizedStatus === "In Use"
                              ? "Checkin"
                              : "Assign"}
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* UPDATED: View More Button Logic */}
          {/* Now shows if hasMore is true, even if filteredEquipment is empty */}
          {hasMore && !loading && (
            <div className="text-center mt-8">
              <Button
                className="bg-button-coloured px-6 py-2"
                onClick={loadMore}
                disabled={isLoadingMore}
              >
                {isLoadingMore ? "Loading..." : "View More"}
              </Button>
            </div>
          )}
        </>
      )}

      <EquipmentDetailsModal
        open={showModal}
        onOpenChange={setShowModal}
        equipment={viewEquipment}
        onPublish={() => handlePublishEquipment(viewEquipment)}
      />
    </div>
  );
};

export default EquipmentPage;
